import { createTheme } from '@mui/material/styles';
import { Alert, Dialog, DialogContent, Grid, CircularProgress, DialogActions, Button, DialogTitle, DialogContentText, TextField, MenuItem, Snackbar, useMediaQuery } from "@mui/material";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IMasterClass from "../../types/MasterClass";
import { useRef, useState } from "react";
import ConflictoDialog from "../agenda/dialogs/ConflictoDialog";
import { ConflictoAgenda, comprobaciones, leerConflicto, sinConfirmaciones } from "../../utils/Disponibilidad";
import AgendaService from '../../services/AgendaService';
import { useNavigate } from "react-router-dom";
import ClaseUtils from '../../utils/Clase';
import ConfigUtils from '../../utils/Config';
import IAlert from '../../types/Alert';
import ISala from '../../types/Sala';
import SelectorClases from './SelectorClases';
import IActividad from '../../types/Actividad';
import * as constantes from "../../common/Constantes";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { aDateOInvalida, esFechaValida, ValorFecha } from '../../utils/FechaSelector';

const locale = constantes.locale;

type EditActivitiesProps = {
    masterClasses: IMasterClass[],
    onClose: () => void,
    callBack: () => void,
    onError: (mensaje: string) => void, // la alerta la pinta el padre: al cerrar la edición este componente se desmonta
    masterIds?: string[], // desde la ficha de una clase: las clases ya elegidas (ella o ella y su pareja), sin el paso de selección
    sesionesPorClase?: Record<string, Date[]> // fechas de las sesiones de cada clase (para su primera clase real)
}

const EditActivities = (props: EditActivitiesProps) => {
    const theme = createTheme();
    // Móvil: los diálogos grandes de la sección ocupan toda la pantalla para poder trabajar con todo el ancho
    const pantallaMovil = useMediaQuery(theme.breakpoints.down('sm'));
    const history = useNavigate();

    const [salas] = useState<Array<ISala>>(ConfigUtils.getSalas());
    const [actividades] = useState<Array<IActividad>>(ConfigUtils.getActividades());

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    // No disponible del profesional nuevo o sala nueva ocupada: se pregunta y, si se confirma, se reenvía sin esa
    // comprobación.
    const [conflicto, setConflicto] = useState<ConflictoAgenda | null>(null);
    const confirmaciones = useRef(sinConfirmaciones());
    const [checked, setChecked] = useState<Array<string>>(props.masterIds ?? []);
    const [modalSelectEdit, setModalSelectEdit] = useState(!!props.masterIds);
    const [modalEditProfesor, setModalEditProfesor] = useState(false);
    const [modalEditSala, setModalEditSala] = useState(false);
    const [smartEdit, setSmartEdit] = useState(false);
    const [especialistaClases, setEspecialistaClases] = useState<Array<number>>([]);
    const [salaClases, setSalaClases] = useState<Array<number>>([]);
    const [modalEditDates, setModalEditDates] = useState(false);
    const [datesEnd, setDatesEnd] = useState<Array<Date>>([]);

    const editFechaFinal = () => {
        if (checked.length === 0) {
            setAlert({ open: true, severity: 'warning', msg: 'No se han seleccionado clases a editar' });
            setModalSelectEdit(false);
            return;
        }
        setModalSelectEdit(false);
        const masterChecked = props.masterClasses.filter(m => m._id === checked[0])[0];
        const terminarRepetir = [];
        terminarRepetir.push(masterChecked.terminarRepetir);
        if (checked.length === 1) {
            setSmartEdit(true);
            setDatesEnd(datesEnd);
            setModalEditDates(true);
        } else {
            let continuar: boolean = false;
            for (let i = 1; i < checked.length; i++) {
                const nextMaster = props.masterClasses.filter(m => m._id === checked[i])[0];
                if (new Date(masterChecked.terminarRepetir).toDateString() === new Date(nextMaster.terminarRepetir).toDateString() && !continuar) {
                    setSmartEdit(true);
                    terminarRepetir.push(nextMaster.terminarRepetir);
                } else {
                    setSmartEdit(false);
                    continuar = true;
                    terminarRepetir.push(nextMaster.terminarRepetir);
                }
            }
        }
        setDatesEnd(terminarRepetir);
        setModalEditDates(true);
    };

    const editProfesor = () => {
        if (checked.length === 0) {
            setAlert({ open: true, severity: 'warning', msg: 'No se han seleccionado clases a editar' });
            setModalSelectEdit(false);
            return;
        }
        setModalSelectEdit(false);
        const masterChecked = props.masterClasses.filter(m => m._id === checked[0])[0];
        const profesorClases = [];
        profesorClases.push(masterChecked.especialistaId);
        if (checked.length === 1) {
            setSmartEdit(true);
            setEspecialistaClases(profesorClases);
            setModalEditProfesor(true);
        } else {
            let continuar: boolean = false;
            for (let i = 1; i < checked.length; i++) {
                const nextMaster = props.masterClasses.filter(m => m._id === checked[i])[0];
                if (masterChecked.especialistaId === nextMaster.especialistaId && !continuar) {
                    setSmartEdit(true);
                    profesorClases.push(nextMaster.especialistaId);
                } else {
                    setSmartEdit(false);
                    continuar = true;
                    profesorClases.push(nextMaster.especialistaId);
                }
            }
        }
        setEspecialistaClases(profesorClases);
        setModalEditProfesor(true);
    };

    const editSala = () => {
        if (checked.length === 0) {
            setAlert({ open: true, severity: 'warning', msg: 'No se han seleccionado clases a editar' });
            setModalSelectEdit(false);
            return;
        }
        setModalSelectEdit(false);
        const masterChecked = props.masterClasses.filter(m => m._id === checked[0])[0];
        const salas = [];
        salas.push(masterChecked.salaId);
        if (checked.length === 1) {
            setSmartEdit(true);
            setSalaClases(salas);
            setModalEditSala(true);
        } else {
            let continuar: boolean = false;
            for (let i = 1; i < checked.length; i++) {
                const nextMaster = props.masterClasses.filter(m => m._id === checked[i])[0];
                if (masterChecked.salaId === nextMaster.salaId && !continuar) {
                    setSmartEdit(true);
                    salas.push(nextMaster.salaId);
                } else {
                    setSmartEdit(false);
                    continuar = true;
                    salas.push(nextMaster.salaId);
                }
            }
        }
        setSalaClases(salas);
        setModalEditSala(true);
    };

    const close = () => {
        setChecked([]);
        setModalSelectEdit(false);
        setModalEditProfesor(false);
        setModalEditSala(false);
        setModalEditDates(false);
        setSmartEdit(false);
        setEspecialistaClases([]);
        setSalaClases([]);
        setDatesEnd([]);
        props.onClose();
    };

    const updateClass = () => {
        setLoading(true);
        let modifiedMasters: IMasterClass[] = [];
        let type: number = 0;

        if (modalEditDates) {
            type = 0;
            if (smartEdit) {

                for (let i = 0; i < checked.length; i++) {
                    let master: IMasterClass = { ...props.masterClasses.filter(x => x._id === checked[i])[0] };

                    // Fecha vacía o a medio escribir: no se envía (llegaba a la API como null)
                    if (!esFechaValida(datesEnd[0])) {
                        setAlert({ open: true, severity: 'warning', msg: 'Indica una fecha final válida' });
                        setLoading(false);
                        return;
                    }
                    // Una fecha final pasada borraría las sesiones ya dadas desde ese día
                    if (moment(datesEnd[0]).isBefore(moment(), 'day')) {
                        setAlert({ open: true, severity: 'warning', msg: 'La fecha final no puede ser anterior a hoy' });
                        setLoading(false);
                        return;
                    }
                    //Comprobar que la fecha final es posterior a la inicial
                    if (new Date(new Date(master.start).toDateString()) > new Date(new Date(datesEnd[0]).toDateString())) {
                        setAlert({ open: true, severity: 'warning', msg: 'La fecha final no puede ser menos que la inicial' });
                        setLoading(false);
                        return;
                    }

                    master.terminarRepetir = datesEnd[0];
                    modifiedMasters.push(master);
                }

            } else {

                for (let i = 0; i < checked.length; i++) {
                    let master: IMasterClass = { ...props.masterClasses.filter(x => x._id === checked[i])[0] };

                    // Fecha vacía o a medio escribir: no se envía (llegaba a la API como null)
                    if (!esFechaValida(datesEnd[i])) {
                        setAlert({ open: true, severity: 'warning', msg: 'Indica una fecha final válida para todas las clases' });
                        setLoading(false);
                        return;
                    }
                    if (moment(datesEnd[i]).isBefore(moment(), 'day')) {
                        setAlert({ open: true, severity: 'warning', msg: 'La fecha final no puede ser anterior a hoy' });
                        setLoading(false);
                        return;
                    }
                    //Comprobar que la fecha final es posterior a la inicial
                    if (new Date(new Date(master.start).toDateString()) > new Date(new Date(datesEnd[i]).toDateString())) {
                        setAlert({ open: true, severity: 'warning', msg: 'La fecha final no puede ser menos que la inicial' });
                        setLoading(false);
                        return;
                    }

                    master.terminarRepetir = datesEnd[i];
                    modifiedMasters.push(master);
                }
            }
        }
        if (modalEditProfesor) {
            type = 1;
            if (smartEdit) {
                checked.map(c => {
                    let master: IMasterClass = { ...props.masterClasses.filter(x => x._id === c)[0] };
                    master.especialistaId = especialistaClases[0];
                    modifiedMasters.push(master);
                    return master;
                });
            } else {
                checked.map((c, index) => {
                    let master: IMasterClass = { ...props.masterClasses.filter(x => x._id === c)[0] };
                    master.especialistaId = especialistaClases[index];
                    modifiedMasters.push(master);
                    return master;
                });
            }
        }
        if (modalEditSala) {
            type = 2;
            if (smartEdit) {
                checked.map(c => {
                    let master: IMasterClass = { ...props.masterClasses.filter(x => x._id === c)[0] };
                    master.salaId = salaClases[0];
                    modifiedMasters.push(master);
                    return master;
                });
            } else {
                checked.map((c, index) => {
                    let master: IMasterClass = { ...props.masterClasses.filter(x => x._id === c)[0] };
                    master.salaId = salaClases[index];
                    modifiedMasters.push(master);
                    return master;
                });
            }
        }

        let preguntar = false;
        AgendaService.updateMasterClasses(modifiedMasters, type, comprobaciones(confirmaciones.current))
            .then((response: any) => {
                confirmaciones.current = sinConfirmaciones();
                props.callBack(); // el padre vuelve a cargar las clases (sin recargar la aplicación)
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else if (leerConflicto(e)) {
                    // Se pregunta con la edición abierta, para poder reenviarla tal cual
                    preguntar = true;
                    setConflicto(leerConflicto(e));
                } else if (e.response?.status === 400) {
                    // Validación de la API (profesional ocupado, etc.): se avisa, no es un error
                    confirmaciones.current = sinConfirmaciones();
                    props.onError(e.response.data?.message || 'Error al modificar las clases');
                } else {
                    confirmaciones.current = sinConfirmaciones();
                    const error: ILog = { method: 'EditActivities.Component.updateClass', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    props.onError(e.response?.data?.message || 'Error al modificar las clases');
                }
            })
            .finally(() => {
                setLoading(false);
                if (!preguntar) close();
            });
    };

    const onChangeEspecialistaClase = (event: any, index: number) => {
        const especialistas = [...especialistaClases];
        especialistas[index] = parseInt(event.target.value);
        setEspecialistaClases(especialistas);
    };

    const onChangeSalaClase = (event: any, index: number) => {
        const salas = [...salaClases];
        salas[index] = parseInt(event.target.value);
        setSalaClases(salas);
    };

    const onChangeFechaFinalClase = (date: ValorFecha, index: number) => {
        const fechas = [...datesEnd]
        fechas[index] = aDateOInvalida(date);
        setDatesEnd(fechas);
    };

    // Valor de las clases elegidas para una opción de «¿Qué necesitas editar?»: el común o «varios»
    const valorActual = (leer: (master: IMasterClass) => string): string => {
        const valores = Array.from(new Set(checked
            .map(id => props.masterClasses.find(m => m._id === id))
            .filter((m): m is IMasterClass => m !== undefined)
            .map(leer)));
        return valores.length === 1 ? valores[0] : 'varios';
    };

    // Clases elegidas cuya pareja (creada junto con ella) no está elegida: si se cambia la fecha final solo de una, dejan de
    // reconocerse como pareja en la ficha. Se avisa en el diálogo de la fecha final, sin impedir guardar.
    const diaYHora = (master: IMasterClass) => `${constantes.weekNames[master.weekDay].toLowerCase()} a las ${moment(master.start).format('H:mm')}`;
    const parejasSinElegir = checked.flatMap(id => {
        const master = props.masterClasses.find(m => m._id === id);
        if (!master) return [];
        const fuera = ClaseUtils.parejasDe(master, props.masterClasses).filter(p => !checked.includes(p._id!));
        return fuera.length > 0 ? [{ master, fuera }] : [];
    });
    const avisoPareja = parejasSinElegir.length === 0 ? '' :
        parejasSinElegir.length === 1 && checked.length === 1
            ? `Esta clase se creó junto con ${parejasSinElegir[0].fuera.map(p => `la del ${diaYHora(p)}`).join(' y ')}. Si cambias la fecha final solo de esta, dejarán de tratarse como pareja y desde la ficha ya no se ofrecerá cambiar las dos a la vez.`
            : `${parejasSinElegir.map(({ master, fuera }) => `La clase del ${diaYHora(master)} se creó junto con ${fuera.map(p => `la del ${diaYHora(p)}`).join(' y ')}.`).join(' ')} Si cambias la fecha final solo de estas clases, dejarán de tratarse como pareja y desde la ficha ya no se ofrecerá cambiarlas a la vez.`;

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    try {

        return (
            <>
                {/* SELECCION DE CLASES */}
                <Dialog open={!props.masterIds} maxWidth="md" fullWidth fullScreen={pantallaMovil} onClose={close}>
                    <DialogTitle sx={{ pb: 0 }}>Elige las clases a editar</DialogTitle>
                    <DialogContent>
                        <SelectorClases masterClasses={props.masterClasses} sesionesPorClase={props.sesionesPorClase} checked={checked} onChange={setChecked} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close}>Cancelar</Button>
                        <Button variant="contained" onClick={() => {
                            if (checked.length === 0) {
                                setAlert({ open: true, severity: 'warning', msg: 'No se han seleccionado clases a modificar' });
                                return;
                            } else {
                                setModalSelectEdit(true);
                            }}}
                        >
                            {checked.length > 0 ? `Continuar (${checked.length})` : 'Continuar'}
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* SELECCION EDICION */}
                <Dialog open={modalSelectEdit} maxWidth="lg">
                    <DialogTitle>Edición de clases</DialogTitle>
                    <>
                        <DialogContent sx={{ paddingBottom: 0 }}>
                            <DialogContentText>¿Qué necesitas editar de las clases?</DialogContentText>
                            {
                                checked.map((item) => {
                                    const clase = props.masterClasses.find(x => x._id === item);
                                    const actividad = clase && ClaseUtils.actividadDe(actividades, clase.title);
                                    return (clase === undefined || !actividad ? null : <p key={item}>{`${actividad.title} - ${ClaseUtils.formatClassTitle(clase)}`}</p>);
                                })
                            }
                        </DialogContent>
                        <DialogActions sx={{ flexDirection: "column" }}>
                            {/* Cada opción con su valor actual (o «varios» si las clases elegidas no coinciden) */}
                            <Button onClick={editFechaFinal} sx={{ textTransform: 'none' }}>{`Fecha final · ${valorActual(m => moment(m.terminarRepetir).format('DD/MM/YYYY'))}`}</Button>
                            <Button onClick={editProfesor} sx={{ textTransform: 'none' }}>{`Especialista · ${valorActual(m => ConfigUtils.getEmpleados().find(e => e.internalID === m.especialistaId)?.firstName ?? '—')}`}</Button>
                            <Button onClick={editSala} sx={{ textTransform: 'none' }}>{`Sala · ${valorActual(m => salas.find(s => String(s.internalID) === String(m.salaId))?.title ?? '—')}`}</Button>
                            {/* Desde la ficha no hay paso de selección al que volver */}
                            <Button onClick={() => { if (props.masterIds) close(); else setModalSelectEdit(false); }}>Cancelar</Button>
                        </DialogActions>
                    </>
                </Dialog>
                {/* EDICION PROFESOR */}
                <Dialog open={modalEditProfesor} maxWidth="sm" fullWidth>
                    <DialogTitle>Cambio de especialista</DialogTitle>
                    <DialogContent>
                        {smartEdit ?
                            <TextField select value={especialistaClases[0]} variant="outlined" label="Especialista" margin="dense" fullWidth required onChange={(event) => setEspecialistaClases([parseInt(event.target.value)])} >
                                {
                                    ConfigUtils.getEmpleados().filter(e => e.activo == true).map((empleado: any) => {
                                        return (
                                            <MenuItem key={empleado.internalID} value={empleado.internalID}>{empleado.firstName}</MenuItem>
                                        );
                                    })
                                }
                            </TextField> :
                            <>
                                <DialogContentText id="alert-dialog-description">Cambia el especialista por clase</DialogContentText>
                                {
                                    checked.map((item, index) => {
                                        const master: IMasterClass = props.masterClasses.filter(x => x._id === item)[0];
                                        return (
                                            <div key={index}>
                                                <p>{`${ClaseUtils.actividadDe(actividades, master.title)?.title ?? ''} - ${ClaseUtils.formatClassTitle(master)}`}</p>
                                                <TextField select value={especialistaClases[index]} variant="outlined" label="Especialista" margin="dense" fullWidth required onChange={(event) => onChangeEspecialistaClase(event, index)} >
                                                    {
                                                        ConfigUtils.getEmpleados().filter(e => e.activo == true).map((empleado: any) => {
                                                            return (
                                                                <MenuItem key={empleado.internalID} value={empleado.internalID}>{empleado.firstName}</MenuItem>
                                                            );
                                                        })
                                                    }
                                                </TextField>
                                            </div>
                                        )
                                    })
                                }
                            </>
                        }

                    </DialogContent>
                    {loading ?
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :
                        <DialogActions>
                            <DialogActions>
                                <Button onClick={updateClass}>Guardar</Button>
                                <Button onClick={() => {setModalEditProfesor(false); setEspecialistaClases([]); setModalSelectEdit(true)}}>Cancelar</Button>
                            </DialogActions>
                        </DialogActions>
                    }
                </Dialog>
                {/* EDICION SALA */}
                <Dialog open={modalEditSala} maxWidth="sm" fullWidth>
                    <DialogTitle>Cambio de sala</DialogTitle>
                    <DialogContent>
                        {smartEdit ?
                            <TextField select value={salaClases[0]} variant="outlined" label="Sala" margin="dense" fullWidth required onChange={(event) => setSalaClases([parseInt(event.target.value)])} >
                                {
                                    salas.map((sala: any) => {
                                        return (
                                            <MenuItem value={sala.internalID}>{sala.title}</MenuItem>
                                        );
                                    })
                                }
                            </TextField> :
                            <>
                                <DialogContentText id="alert-dialog-description">Cambia la sala por clase</DialogContentText>
                                {
                                    checked.map((item, index) => {
                                        const master: IMasterClass = props.masterClasses.filter(x => x._id === item)[0];
                                        return (
                                            <div key={index}>
                                                <p>{`${ClaseUtils.actividadDe(actividades, master.title)?.title ?? ''} - ${ClaseUtils.formatClassTitle(master)}`}</p>
                                                <TextField select value={salaClases[index]} variant="outlined" label="Sala" margin="dense" fullWidth required onChange={(event) => onChangeSalaClase(event, index)} >
                                                    {
                                                        salas.map((sala: any) => {
                                                            return (
                                                                <MenuItem key={sala.internalID} value={sala.internalID}>{sala.title}</MenuItem>
                                                            );
                                                        })
                                                    }
                                                </TextField>
                                            </div>
                                        )
                                    })
                                }
                            </>
                        }

                    </DialogContent>
                    {loading ?
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :
                        <DialogActions>
                            <DialogActions>
                                <Button onClick={updateClass}>Guardar</Button>
                                <Button onClick={() => {setModalEditSala(false); setSalaClases([]); setModalSelectEdit(true)}}>Cancelar</Button>
                            </DialogActions>
                        </DialogActions>
                    }
                </Dialog>
                {/* EDICIÓN FECHAS */}
                <Dialog open={modalEditDates} maxWidth="sm" fullWidth>
                    <DialogTitle>Cambio de fecha final</DialogTitle>
                    <DialogContent>
                        {avisoPareja && <Alert severity="info" sx={{ mb: 2 }}>{avisoPareja}</Alert>}
                        {smartEdit ? 
                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                <DatePicker 
                                    renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                    label="Fecha final"
                                    minDate={moment()}
                                    toolbarFormat="dd-mm-yyyy"
                                    value={datesEnd[0]}
                                    onChange={(date: Moment | null) => setDatesEnd([aDateOInvalida(date)])}
                                />
                            </LocalizationProvider> :
                            <>
                                <DialogContentText>Cambia la fecha final por clase</DialogContentText>
                                {
                                    checked.map((item, index) => {
                                        const master: IMasterClass = props.masterClasses.filter(x => x._id === item)[0];
                                        return (
                                            <div key={index}>
                                                <p>{`${ClaseUtils.actividadDe(actividades, master.title)?.title ?? ''} - ${ClaseUtils.formatClassTitle(master)}`}</p>
                                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                                    <DatePicker 
                                                        renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                                        label="Fecha final"
                                                        minDate={moment()}
                                                        toolbarFormat="dd-mm-yyyy"
                                                        value={datesEnd[index]}
                                                        onChange={(date) => onChangeFechaFinalClase(date, index)}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                        ) 
                                    })
                                }
                            </>

                        }
                    </DialogContent>
                    {loading ?
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :
                        <DialogActions>
                            <DialogActions>
                                <Button onClick={updateClass}>Guardar</Button>
                                <Button onClick={() => {setModalEditDates(false); setDatesEnd([]); setModalSelectEdit(true)}}>Cancelar</Button>
                            </DialogActions>
                        </DialogActions>
                    }
                </Dialog>

                {/* PROFESIONAL NO DISPONIBLE O SALA OCUPADA: ¿modificar igualmente? */}
                <ConflictoDialog
                    conflicto={conflicto}
                    onConfirmar={() => {
                        if (conflicto) confirmaciones.current = { ...confirmaciones.current, [conflicto.tipo]: true };
                        setConflicto(null);
                        updateClass();
                    }}
                    onCancelar={() => { confirmaciones.current = sinConfirmaciones(); setConflicto(null); close(); }}
                />

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </>
        );

    } catch (e: any) {
        const error: ILog = { method: 'EditActivities.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default EditActivities;